import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    type SliderValue,
    formatPercent,
    Slider
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    BellIcon
} from "../../anatomy/icons";

const NAME = "Slider";

const USAGE_CODE = `const [volume, setVolume] = useState<SliderValue>(40);

<Slider
    formatValueText={(value) => \`\${value}%\`}
    title="Volume"
    onValueChange={setVolume}
    value={volume}
/>`;

const RANGE_CODE = `const [age, setAge] = useState<SliderValue>([25, 40]);

<Slider
    thumbTitles={[
        "Minimum age",
        "Maximum age"
    ]}
    onChangeEnd={applyAgeFilter}
    title="Age range"
    onValueChange={setAge}
    value={age}
    min={18}
    max={65}
/>`;

const MARKS_CODE = `<Slider
    marks={[
        {
            title: "Small",
            value: 0
        },
        {
            title: "Default",
            value: 1
        },
        {
            title: "Large",
            value: 2
        }
    ]}
    defaultValue={1}
    title="Font size"
    isShowTicks={true}
    max={2}
/>`;

const TYPES = [
    "primary",
    "neutral",
    "success",
    "info",
    "warning",
    "danger"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const FILL_PERCENT = 100;

const COMPACT_STYLE = {
    width: 200
};

const getRangeText = (value: SliderValue) => Array.isArray(value) ? `${value[0]} – ${value[1]}` : String(value);

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("sliderPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("sliderPage.stepsTitle"),
        id: "steps"
    },
    {
        title: t("sliderPage.valueTitle"),
        id: "value"
    },
    {
        title: t("sliderPage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("sliderPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SliderPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        volume,
        setVolume
    ] = useState<SliderValue>(40);

    const [
        age,
        setAge
    ] = useState<SliderValue>([
        25,
        40
    ]);

    const [
        appliedAge,
        setAppliedAge
    ] = useState<SliderValue>([
        25,
        40
    ]);

    const formatPercentText = (value: number) => formatPercent({
        value: value / FILL_PERCENT,
        locale: activeLocale
    });

    const formatMinutesText = (value: number) => t("sliderPage.minutes", [
        value
    ]);

    const fontMarks = [
        {
            title: t("sliderPage.fontSmall"),
            value: 0
        },
        {
            title: t("sliderPage.fontDefault"),
            value: 1
        },
        {
            title: t("sliderPage.fontLarge"),
            value: 2
        },
        {
            title: t("sliderPage.fontLargest"),
            value: 3
        }
    ];

    return <Article>
        <ComponentHeader
            lead={t("sliderPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("sliderPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("sliderPage.overviewCaption")}
                isStacked={true}
            >
                <Slider
                    formatValueText={formatPercentText}
                    title={t("sliderPage.volume")}
                    onValueChange={setVolume}
                    value={volume}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("sliderPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("sliderPage.rangeText")}
            </Paragraph>
            <Preview
                caption={t("sliderPage.rangeCaption", [
                    getRangeText(appliedAge)
                ])}
                isStacked={true}
            >
                <Slider
                    title={t("sliderPage.ageRange")}
                    thumbTitles={[
                        t("sliderPage.minAge"),
                        t("sliderPage.maxAge")
                    ]}
                    onChangeEnd={setAppliedAge}
                    isShowMinMaxLabels={true}
                    onValueChange={setAge}
                    value={age}
                    max={65}
                    min={18}
                />
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sliderPage.stepsTitle")}
            id="steps"
        >
            <Paragraph>
                {t("sliderPage.stepsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Slider
                    formatValueText={formatPercentText}
                    title={t("sliderPage.brightness")}
                    isShowTicks={true}
                    defaultValue={60}
                    step={10}
                />
                <Slider
                    title={t("sliderPage.fontSize")}
                    isShowTicks={true}
                    marks={fontMarks}
                    defaultValue={1}
                    max={3}
                />
                <Slider
                    formatValueText={formatMinutesText}
                    title={t("sliderPage.session")}
                    isShowMinMaxLabels={true}
                    defaultValue={30}
                    max={120}
                    min={5}
                />
            </Preview>
            <CodeBlock
                code={MARKS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sliderPage.valueTitle")}
            id="value"
        >
            <Paragraph>
                {t("sliderPage.valueText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Slider
                    hintText={t("sliderPage.volumeHint")}
                    formatValueText={formatPercentText}
                    title={t("sliderPage.volume")}
                    defaultValue={35}
                />
                <Slider
                    accessibilityLabel={t("sliderPage.volume")}
                    formatValueText={formatPercentText}
                    tooltipBehaviour="dragging"
                    defaultValue={70}
                    icon={BellIcon}
                />
                <Slider
                    thumbTitles={[
                        t("sliderPage.minAge"),
                        t("sliderPage.maxAge")
                    ]}
                    tooltipBehaviour="always"
                    defaultValue={[
                        30,
                        75
                    ]}
                />
            </Preview>
        </Section>
        <Section
            title={t("sliderPage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("sliderPage.appearanceText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <Slider
                        formatValueText={formatPercentText}
                        defaultValue={50}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
            <Preview>
                {
                    TYPES.map((type) => <Slider
                        formatValueText={formatPercentText}
                        style={COMPACT_STYLE}
                        defaultValue={60}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
            <Preview>
                <Slider
                    formatValueText={formatPercentText}
                    title={t("sliderPage.disabled")}
                    style={COMPACT_STYLE}
                    isDisabled={true}
                    defaultValue={40}
                />
                <Slider
                    title={t("sliderPage.disabled")}
                    thumbTitles={[
                        t("sliderPage.minAge"),
                        t("sliderPage.maxAge")
                    ]}
                    style={COMPACT_STYLE}
                    isDisabled={true}
                    defaultValue={[
                        20,
                        70
                    ]}
                />
            </Preview>
        </Section>
        <Section
            title={t("sliderPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("sliderPage.orientationText")}
            </Paragraph>
            <Preview>
                <Slider
                    accessibilityLabel={t("sliderPage.volume")}
                    formatValueText={formatPercentText}
                    tooltipBehaviour="dragging"
                    orientation="vertical"
                    isShowValue={true}
                    defaultValue={60}
                    icon={BellIcon}
                />
                <Slider
                    thumbTitles={[
                        t("sliderPage.minAge"),
                        t("sliderPage.maxAge")
                    ]}
                    orientation="vertical"
                    isShowValue={true}
                    defaultValue={[
                        20,
                        70
                    ]}
                />
                <Slider
                    accessibilityLabel={t("sliderPage.volume")}
                    formatValueText={formatPercentText}
                    orientation="vertical"
                    isShowValue={true}
                    isDisabled={true}
                    defaultValue={60}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("sliderPage.a11yRole"),
                    t("sliderPage.a11yKeyboard"),
                    t("sliderPage.a11yName"),
                    t("sliderPage.a11yNative"),
                    t("sliderPage.a11yDisabled"),
                    t("sliderPage.a11yHidden"),
                    t("sliderPage.a11yCommit")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("sliderPage.propValue"),
                        type: "number | [number, number]",
                        name: "value"
                    },
                    {
                        description: t("sliderPage.propDefaultValue"),
                        type: "number | [number, number]",
                        name: "defaultValue",
                        defaultValue: "min"
                    },
                    {
                        description: t("sliderPage.propOnValueChange"),
                        type: "(value: SliderValue) => void",
                        name: "onValueChange"
                    },
                    {
                        description: t("sliderPage.propOnChangeEnd"),
                        type: "(value: SliderValue) => void",
                        name: "onChangeEnd"
                    },
                    {
                        description: t("sliderPage.propMin"),
                        defaultValue: "0",
                        type: "number",
                        name: "min"
                    },
                    {
                        description: t("sliderPage.propMax"),
                        defaultValue: "100",
                        type: "number",
                        name: "max"
                    },
                    {
                        description: t("sliderPage.propStep"),
                        defaultValue: "1",
                        type: "number",
                        name: "step"
                    },
                    {
                        type: "Array<{ value: number; title?: string }>",
                        description: t("sliderPage.propMarks"),
                        name: "marks"
                    },
                    {
                        description: t("sliderPage.propMinStepsBetweenThumbs"),
                        name: "minStepsBetweenThumbs",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"info\" | \"warning\" | \"danger\"",
                        description: t("sliderPage.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("sliderPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("sliderPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("sliderPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("sliderPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("sliderPage.propThumbTitles"),
                        type: "[string, string]",
                        name: "thumbTitles"
                    },
                    {
                        description: t("sliderPage.propIsShowValue"),
                        name: "isShowValue",
                        type: "boolean"
                    },
                    {
                        description: t("sliderPage.propFormatValueText"),
                        type: "(value: number) => string",
                        name: "formatValueText"
                    },
                    {
                        description: t("sliderPage.propTooltipBehaviour"),
                        type: "\"hidden\" | \"dragging\" | \"always\"",
                        defaultValue: "\"dragging\"",
                        name: "tooltipBehaviour"
                    },
                    {
                        description: t("sliderPage.propIsShowTicks"),
                        defaultValue: "false",
                        name: "isShowTicks",
                        type: "boolean"
                    },
                    {
                        description: t("sliderPage.propIsShowMinMaxLabels"),
                        name: "isShowMinMaxLabels",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("sliderPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("sliderPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        description: t("sliderPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("sliderPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("sliderPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("sliderPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sliderPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SliderPage;
